/**
 * Cycle jour/nuit — **logique pure** (§4, §7).
 *
 * Séparé du rendu à dessein : `render/sky.ts` importe PixiJS, donc rien de testable ne
 * doit y vivre. Ici, une fonction du temps vers une palette, vérifiable sans navigateur
 * — c'est ce qui permet d'affirmer que le ciel ne saute jamais d'une couleur à l'autre,
 * plutôt que de l'espérer.
 *
 * Le cycle repose sur un horodatage ABSOLU, comme le tick économique : un onglet masqué
 * vingt minutes retrouve le bon moment du jour en revenant, et deux joueurs ouvrant le
 * jeu au même instant voient le même ciel — ce qui comptera le jour où l'on affiche la
 * ville d'un autre (§9).
 */

import { lerp } from './easing';

/** Durée d'une journée de jeu. Six minutes : assez long pour ne pas clignoter,
 *  assez court pour qu'une session voie passer la nuit. */
export const DAY_LENGTH_MS = 6 * 60 * 1000;

interface SkyStop {
  /** Position dans la journée, de 0 (minuit) à 1. */
  readonly at: number;
  readonly top: readonly [number, number, number];
  readonly bottom: readonly [number, number, number];
  /** Opacité des étoiles à ce moment. */
  readonly stars: number;
}

/**
 * Palette du cycle. Les teintes restent dans le registre du jeu (D§2) : on ne passe
 * jamais par un ciel photoréaliste, qui jurerait avec des sprites dessinés.
 */
const CYCLE: readonly SkyStop[] = [
  // Nuit profonde, courte : elle occupait 40 % du cycle et le jeu paraissait nocturne
  // en permanence. Elle en occupe maintenant environ un quart.
  { at: 0.0, top: [10, 16, 36], bottom: [24, 34, 64], stars: 1 },
  { at: 0.08, top: [16, 22, 48], bottom: [40, 44, 84], stars: 0.95 },

  /*
    AUBE puis CRÉPUSCULE : quatre paliers chacun, là où deux suffisaient au reste.

    C'est le moment où l'éclairage rasant donne le meilleur des volumes stylisés, et
    une simple interpolation entre nuit et jour le traverse en ligne droite — donc en
    gris. Les paliers intermédiaires font passer le ciel par le violet, puis l'orange
    chaud, puis le doré, avant de rejoindre le bleu. C'est ce détour dans la teinte,
    et non la durée, qui rend le moment beau.
  */
  { at: 0.12, top: [46, 38, 92], bottom: [150, 84, 118], stars: 0.55 },
  { at: 0.16, top: [86, 62, 128], bottom: [232, 132, 106], stars: 0.2 },
  { at: 0.2, top: [128, 122, 190], bottom: [252, 186, 128], stars: 0.05 },
  { at: 0.26, top: [116, 168, 226], bottom: [226, 226, 224], stars: 0 },

  // Plein jour, large : c'est l'état par défaut du jeu.
  { at: 0.4, top: [74, 146, 220], bottom: [172, 210, 238], stars: 0 },
  { at: 0.62, top: [70, 142, 218], bottom: [178, 214, 240], stars: 0 },

  { at: 0.74, top: [104, 150, 214], bottom: [246, 206, 150], stars: 0 },
  { at: 0.8, top: [126, 110, 178], bottom: [250, 158, 96], stars: 0.05 },
  { at: 0.85, top: [78, 56, 118], bottom: [206, 100, 96], stars: 0.3 },
  { at: 0.9, top: [40, 34, 82], bottom: [110, 62, 104], stars: 0.7 },

  { at: 1.0, top: [10, 16, 36], bottom: [24, 34, 64], stars: 1 },
];

function mixChannel(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  t: number,
  i: 0 | 1 | 2,
): number {
  return Math.round(lerp(a[i], b[i], t));
}

export interface SkyState {
  readonly top: readonly [number, number, number];
  readonly bottom: readonly [number, number, number];
  readonly stars: number;
  /** Position dans la journée, 0 à 1. Exposée pour les tests et la sonde. */
  readonly phase: number;
}

/**
 * État du ciel à un instant donné — **fonction pure**, donc testable sans navigateur.
 *
 * `now` est un horodatage absolu : deux appareils ouverts au même moment voient le
 * même ciel, ce qui compte le jour où l'on montre la ville d'un autre joueur (§9).
 */
/**
 * Décalage de teinte du ciel pour une ère donnée, en degrés (ROADMAP 3.9).
 *
 * ══ Pourquoi une TEINTE et pas une palette écrite ════════════════════════════
 *
 * Le §1 veut que chaque ère donne envie de recommencer. La ville repart de zéro, le
 * bonus reste — mais tout se ressemble, et c'est le sentiment de répétition qui use un
 * idle. Une lumière différente suffit à dire « c'est une autre partie » sans toucher à
 * une seule règle.
 *
 * Une table « ère → palette » aurait deux défauts : elle se termine (le joueur de la
 * quinzième ère retrouve la première), et elle demande d'inventer des couleurs qui
 * s'accordent avec les bâtiments. Un décalage BORNÉ de la teinte existante garde
 * l'harmonie de la palette d'origine par construction, et ne s'épuise jamais.
 *
 * ⚠️ **L'ère 0 vaut exactement zéro**, et ce n'est pas un détail : la palette de la
 * première partie est celle qui a été composée, mesurée et validée. Une ère lointaine la
 * décale ; elle ne la remplace pas.
 *
 * L'amplitude est volontairement petite — ±24° — parce qu'au-delà le ciel cesse d'être
 * un ciel. Le pas premier (53) évite qu'une ère sur deux retombe sur la même teinte.
 */
export function eraHueShift(era: number): number {
  const level = Math.max(0, Math.floor(era));
  if (level === 0) return 0;
  /*
    ⚠️ **L'ÉCART DORÉ, et pas un pas modulaire.** Première version : `(ère × 53) % 49`.
    Mesurée sur les douze premières ères, elle donnait une rampe de 4° par ère —
    imperceptible — et l'ère 6 retombait exactement sur la teinte de l'ère 0.

    Le nombre d'or est la façon connue de disperser une suite de teintes : chaque
    nouvelle valeur tombe dans le plus grand intervalle encore libre, quel que soit le
    nombre d'ères déjà jouées. Mesuré : 6°, -13°, 17°, -1°, -20°, 10°, -8°, 21° — écart
    minimal de 6° entre deux ères consécutives sur les vingt et une premières.
  */
  return Math.round(((level * 0.618033988749895) % 1) * 48) - 24;
}

/** Rotation de teinte d'une couleur RVB, en degrés. Rend la couleur telle quelle à 0°. */
function rotateHue(
  rgb: readonly [number, number, number],
  degrees: number,
): [number, number, number] {
  if (degrees === 0) return [rgb[0], rgb[1], rgb[2]];

  /*
    Matrice de rotation de teinte dans l'espace RVB — la même que celle des filtres CSS
    `hue-rotate`. On l'utilise plutôt qu'un passage par TSL parce qu'elle est linéaire :
    pas de division par la saturation, donc pas de cas limite sur un gris de nuit, qui
    est exactement ce que la palette contient au plus noir.
  */
  const angle = (degrees * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  const m = [
    0.213 + cos * 0.787 - sin * 0.213,
    0.715 - cos * 0.715 - sin * 0.715,
    0.072 - cos * 0.072 + sin * 0.928,
    0.213 - cos * 0.213 + sin * 0.143,
    0.715 + cos * 0.285 + sin * 0.14,
    0.072 - cos * 0.072 - sin * 0.283,
    0.213 - cos * 0.213 - sin * 0.787,
    0.715 - cos * 0.715 + sin * 0.715,
    0.072 + cos * 0.928 + sin * 0.072,
  ];

  const clamp = (value: number): number => Math.max(0, Math.min(255, Math.round(value)));
  return [
    clamp(rgb[0] * (m[0] ?? 1) + rgb[1] * (m[1] ?? 0) + rgb[2] * (m[2] ?? 0)),
    clamp(rgb[0] * (m[3] ?? 0) + rgb[1] * (m[4] ?? 1) + rgb[2] * (m[5] ?? 0)),
    clamp(rgb[0] * (m[6] ?? 0) + rgb[1] * (m[7] ?? 0) + rgb[2] * (m[8] ?? 1)),
  ];
}

export function skyAt(now: number, hueShift = 0): SkyState {
  const phase =
    ((now % DAY_LENGTH_MS) + DAY_LENGTH_MS) / DAY_LENGTH_MS -
    Math.floor(((now % DAY_LENGTH_MS) + DAY_LENGTH_MS) / DAY_LENGTH_MS);

  let from = CYCLE[0] ?? { at: 0, top: [0, 0, 0], bottom: [0, 0, 0], stars: 1 };
  let to = from;

  for (let i = 0; i < CYCLE.length - 1; i++) {
    const a = CYCLE[i];
    const b = CYCLE[i + 1];
    if (a === undefined || b === undefined) continue;
    if (phase >= a.at && phase <= b.at) {
      from = a;
      to = b;
      break;
    }
  }

  const span = to.at - from.at;
  const t = span <= 0 ? 0 : (phase - from.at) / span;

  /*
    La teinte d'ère s'applique APRÈS le mélange des deux paliers, jamais avant : décaler
    les paliers séparément ferait dériver la couleur pendant la transition, et le ciel
    changerait de teinte au fil de la journée au lieu de la garder.
  */
  return {
    top: rotateHue(
      [
        mixChannel(from.top, to.top, t, 0),
        mixChannel(from.top, to.top, t, 1),
        mixChannel(from.top, to.top, t, 2),
      ],
      hueShift,
    ),
    bottom: rotateHue(
      [
        mixChannel(from.bottom, to.bottom, t, 0),
        mixChannel(from.bottom, to.bottom, t, 1),
        mixChannel(from.bottom, to.bottom, t, 2),
      ],
      hueShift,
    ),
    stars: lerp(from.stars, to.stars, t),
    phase,
  };
}

/*
  ══ LA LUEUR DES FENÊTRES ══════════════════════════════════════════════════════

  La nuit teintait la ville en bleu, et c'est tout : les tours vitrées devenaient des
  rectangles sombres indiscernables les uns des autres. Le code censé y remédier existait
  — un champ `windowGlow`, lu par l'éclairage de nuit — et **n'a jamais été instancié**.
  Il se lisait comme une feature livrée tout en ne faisant rien, ce qui est pire qu'une
  absence : ça empêche de voir ce qui manque.

  Ce qui suit est la partie PURE de l'effet : quelles fenêtres s'allument, et quand.
  Le rendu n'en connaît que le résultat, un nombre entre 0 et 1.

  ── Pourquoi ce n'est pas simplement « toutes, la nuit » ──────────────────────

  Une ville dont toutes les fenêtres s'allument et s'éteignent ensemble ne ressemble à
  rien de vivant. Ce qui rend une ville nocturne crédible, c'est que **les fenêtres ne
  sont pas d'accord entre elles** : certaines s'allument tôt, d'autres jamais, quelques
  unes s'éteignent au milieu de la nuit parce que quelqu'un s'est couché.
*/

/**
 * Intensité de la lueur d'un bâtiment, de 0 (éteint) à 1 (pleine lueur).
 *
 * **Déterministe, dérivée de la position** — exactement comme l'orientation et la
 * variante d'un bâtiment. Deux chargements de la même sauvegarde donnent la même ville
 * de nuit ; un tirage au sort la ferait scintiller à chaque rechargement, et le joueur
 * verrait sa ville changer sans avoir rien fait.
 *
 * `stars` porte l'avancement de la nuit (0 en plein jour, 1 au plus noir). La lueur ne
 * suit pas cette courbe : elle a son propre seuil par bâtiment, ce qui fait que les
 * fenêtres s'allument **les unes après les autres** au crépuscule au lieu de basculer
 * toutes ensemble.
 */
export function windowGlowAt(col: number, row: number, stars: number): number {
  if (stars <= 0) return 0;

  /*
    Un bruit déterministe et bon marché, dans [0 · 1[.

    Les grands nombres premiers évitent les motifs réguliers : sans eux, un damier
    apparaît, et une ville dont une case sur deux est allumée se voit immédiatement
    comme une texture et non comme des habitants.
  */
  const hash = Math.abs(Math.sin(col * 127.1 + row * 311.7) * 43_758.545_3);
  const noise = hash - Math.floor(hash);

  // Un bâtiment sur six reste éteint toute la nuit. Une ville entièrement allumée n'a
  // pas d'habitants : elle a un interrupteur.
  if (noise < 0.17) return 0;

  /*
    ⚠️ **La lueur doit suivre la NUIT, pas la devancer.**

    Défaut livré en 0.23.0 et vu par Benoit sur sa ville : des taches blanches sur des
    bâtiments en plein ciel bleu. La cause n'est pas dans le rendu — elle est ici.

    La teinte de nuit n'assombrit la scène que de 34 % au plus noir. À mi-nuit
    (`stars = 0,5`), le décor n'a donc perdu que 17 % de sa lumière : il ressemble encore
    au jour. Or les seuils d'allumage s'étalaient entre 0,08 et 0,50, avec une rampe de
    0,25 — à `stars = 0,5`, la quasi-totalité des fenêtres était **déjà à pleine
    puissance**. Des lampes à fond sur un décor diurne : l'œil lit ça comme un bug avant
    de savoir pourquoi.

    Deux corrections, et il fallait les deux :

    1. **Les seuils reculent** — rien ne s'allume avant que la nuit soit à moitié tombée,
       et le dernier retardataire attend 85 %.
    2. **L'intensité est multipliée par `stars`** — la lueur ne peut donc jamais dépasser
       l'obscurité qui la justifie. C'est un couplage, pas un réglage : si la palette de
       nuit change un jour, le rapport tient toujours.
  */
  /*
    Les seuils s'étalent sur le CRÉPUSCULE (0,18 → 0,50) — plus tôt qu'avant : la
    direction artistique du 18 août veut le maximum d'éclairage au crépuscule et à
    l'aube, pas au cœur de la nuit. L'enveloppe `urbanLightAt` fait le reste : elle
    interdit toute lumière tant que la scène est diurne (stars ≤ 0,12), porte le
    plein feu pendant la transition, et tamise au plus noir.
  */
  const threshold = 0.18 + noise * 0.32;
  if (stars <= threshold) return 0;

  const ramp = Math.min(1, (stars - threshold) / 0.14);
  // L'intensité maximale varie aussi : deux fenêtres voisines n'ont pas la même lampe.
  return ramp * (0.55 + noise * 0.45) * urbanLightAt(stars);
}

/**
 * Courbe d'ÉCLAIRAGE URBAIN — la direction artistique de Benoit (18 août) :
 * « le max au crépuscule et le matin, garde un peu d'éclairage la nuit, la journée
 * tu ne laisses que les feux ». `stars` traverse les mêmes valeurs au crépuscule et
 * à l'aube : la symétrie demandée est vraie par construction.
 *
 *   · jour (stars ≤ 0,12)        → 0 — seuls les feux tricolores restent ;
 *   · crépuscule / aube          → montée jusqu'à 1, plein feu de 0,45 à 0,80 ;
 *   · nuit noire (stars → 1)     → redescend à 0,55 — la ville dort, sans s'éteindre.
 */
export function urbanLightAt(stars: number): number {
  if (stars <= 0.12) return 0;
  if (stars < 0.45) return (stars - 0.12) / 0.33;
  if (stars <= 0.8) return 1;
  return 1 - ((stars - 0.8) / 0.2) * 0.45;
}

/**
 * La VIE derrière les fenêtres : elles s'éteignent et se rallument au fil de la nuit.
 *
 * Demande de Benoit (18 août) : « pour les bâtiments, des lumières doivent s'éteindre
 * et s'allumer aléatoirement ». Une fenêtre allumée à intensité fixe toute la nuit se
 * lit comme une texture ; une fenêtre qui s'éteint raconte quelqu'un qui va se coucher.
 *
 * Le temps est FOURNI (l'horloge du monde), jamais lu : la fonction reste pure, donc
 * testable, et deux clients au même instant montrent la même ville — la page visiteur
 * doit voir exactement ce que voit le propriétaire.
 *
 * Chaque bâtiment vit sur ses propres créneaux (9 à 15 s, longueur dérivée de sa
 * position) ; chaque créneau tire au sort « allumé » (trois fois sur quatre), et la
 * bascule entre deux créneaux se fond en ~800 ms — une lampe ne claque pas.
 */
export function windowLitGate(col: number, row: number, now: number): number {
  const seed = Math.abs(Math.sin(col * 91.7 + row * 47.3) * 22_461.271);
  const phase = seed - Math.floor(seed);
  const slotMs = 9000 + phase * 6000;

  const at = now / slotMs + phase;
  const slot = Math.floor(at);
  const within = (at - slot) * slotMs;

  const litIn = (index: number): number => {
    const h = Math.abs(Math.sin(col * 7.7 + row * 13.3 + index * 101.9) * 15_731.743);
    return h - Math.floor(h) < 0.75 ? 1 : 0;
  };

  const current = litIn(slot);
  const previous = litIn(slot - 1);
  if (current === previous) return current;

  // Fondu entre deux créneaux d'états différents : 800 ms, linéaire — assez court
  // pour se lire comme un interrupteur, assez long pour ne pas scintiller.
  const FADE_MS = 800;
  if (within >= FADE_MS) return current;
  return previous + (current - previous) * (within / FADE_MS);
}
